import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useGetContentItemQuery } from 'common/store/modules/api/content-api';
import { LazyImage } from 'common/react/components/LazyImage';
import { useBettingVis } from 'common/react/hooks/use-betting-vis';
import { SponsorSkeleton } from './SponsorSkeleton';

const BETTING_TAG = 'sponsor:betting';

/**
 * Displays a Promo as a sponsor.
 *
 * @param {object} props props
 * @param {string} props.promoId The promo ID to be rendered as a sponsor.
 * @param {string} [props.cssClass] The CSS class to be applied.
 * @param {string} [props.skeletonCssClass] The CSS class to be applied to Skeleton element.
 * @param {object} [props.promoPayload] payload of PROMO to display. in case the PROMO is fetched outside of this component, pass the PROMO object. The promoId prop must be undefined for this to be applied.
 * @param {number} [props.imageHeight] Height of the PROMO image. Default 108 be undefined for this to be applied.
 * @returns {JSX.Element} The Sponsor component.
 */
export const Sponsor = ({
    promoId,
    cssClass = '',
    skeletonCssClass = '',
    promoPayload,
    imageHeight = 108
}) => {
    const sponsorRef = useRef();
    const isBettingVisible = useBettingVis();

    const { data: contentPromo, isFetching: isFetchingPromoData } =
        useGetContentItemQuery(
            {
                type: 'PROMO',
                id: promoId
            },
            { skip: !promoId }
        );

    const promo = promoPayload || contentPromo;

    const isBettingSponsor = promo?.tags?.find(
        (tag) => tag.label === BETTING_TAG
    );
    const promoLink = promo?.links[0];
    const target = promoLink?.external ? '_blank' : null;

    useEffect(() => {
        if (sponsorRef.current) {
            PULSE.app.analytics.addSponsorTracking(sponsorRef.current);
        }
    }, [promo, sponsorRef]);

    if (isBettingSponsor && !isBettingVisible) {
        return null;
    }

    return isFetchingPromoData ? (
        <SponsorSkeleton cssClass={skeletonCssClass} />
    ) : (
        <>
            {promo ? (
                <>
                    {/* <SponsorSkeleton cssClass={cssClass} /> */}
                    <div
                        className={`content-sponsor content-sponsor--without-borders content-sponsor--without-padding ${cssClass}`}
                        ref={sponsorRef}
                    >
                        {promo.title && promo.title !== '' ? (
                            <h2 className="content-sponsor__title">
                                {promo.title}
                            </h2>
                        ) : null}
                        {promoLink?.promoUrl ? (
                            <a
                                className="content-sponsor__image-link js-tracked-sponsor-item"
                                href={promoLink.promoUrl}
                                target={target}
                                title={promo.linkText || ''}
                                data-type={
                                    PULSE.app.common.CONSTANTS.TRACKING
                                        .CONTENT_TYPES.LINEUPS_SPONSOR
                                }
                                data-component-name={
                                    PULSE.app.common.CONSTANTS.TRACKING
                                        .CONTENT_TYPES.TEAM_LINEUP_CONDENSED
                                }
                                data-component-content-type={
                                    PULSE.app.common.CONSTANTS.TRACKING
                                        .CONTENT_TYPES.MATCH_INFO
                                }
                                data-link-text={`${promo.title || ''} ${
                                    promoLink.linkText || ''
                                }`}
                                data-name={promoLink.linkText || ''}
                            >
                                {promo?.promoItem?.onDemandUrl ? (
                                    <LazyImage
                                        url={`${promo.promoItem.onDemandUrl}?height=${imageHeight}`}
                                        cssClass="content-sponsor__image"
                                        objectFit={false}
                                    />
                                ) : null}
                                {promoLink.linkText ? (
                                    <span className="u-screen-reader">
                                        {promoLink.linkText}
                                    </span>
                                ) : null}
                            </a>
                        ) : (
                            <div className="content-sponsor__image-link">
                                {promo?.promoItem?.onDemandUrl ? (
                                    <LazyImage
                                        url={`${promo.promoItem.onDemandUrl}?height=${imageHeight}`}
                                        cssClass="content-sponsor__image"
                                        objectFit={false}
                                    />
                                ) : null}
                                {promoLink?.linkText ? (
                                    <span className="u-screen-reader">
                                        {promoLink.linkText}
                                    </span>
                                ) : null}
                            </div>
                        )}
                    </div>
                </>
            ) : null}
        </>
    );
};

Sponsor.propTypes = {
    promoId: PropTypes.string,
    cssClass: PropTypes.string,
    skeletonCssClass: PropTypes.string,
    promoPayload: PropTypes.object,
    imageHeight: PropTypes.number
};
